import React, { useEffect, useState } from 'react';
import { TextField } from '@mui/material';

const INTEGER_PATTERN = /^[0-9]*$/;
const DECIMAL_PATTERN = /^[0-9]*\.?[0-9]*$/;

const clampValue = (value, min, max) => {
  let result = value;
  if (min != null) result = Math.max(min, result);
  if (max != null) result = Math.min(max, result);
  return result;
};

const parseNumericValue = (raw, { allowDecimal, allowEmpty, min }) => {
  if (raw === '' || raw == null) {
    return allowEmpty ? '' : (min ?? 0);
  }
  const parsed = allowDecimal ? parseFloat(raw) : parseInt(raw, 10);
  if (Number.isNaN(parsed)) {
    return allowEmpty ? '' : (min ?? 0);
  }
  return parsed;
};

const formatDisplayValue = (value, allowEmpty) => {
  if (value === '' || value == null) {
    return allowEmpty ? '' : '';
  }
  return String(value);
};

/**
 * Numeric input that supports mobile editing: select-all on focus and empty while typing.
 */
function NumericTextField({
  value,
  onChange,
  onValueChange,
  min,
  max,
  allowDecimal = false,
  allowEmpty = false,
  name,
  inputProps: inputPropsProp = {},
  InputProps: InputPropsProp = {},
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  ...textFieldProps
}) {
  const [displayValue, setDisplayValue] = useState(() => formatDisplayValue(value, allowEmpty));
  const [isFocused, setIsFocused] = useState(false);

  const pattern = allowDecimal ? DECIMAL_PATTERN : INTEGER_PATTERN;

  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(formatDisplayValue(value, allowEmpty));
    }
  }, [value, isFocused, allowEmpty]);

  const emitChange = (nextValue) => {
    if (onValueChange) {
      onValueChange(nextValue);
    }
    if (onChange) {
      onChange({
        target: {
          name: name || '',
          value: nextValue === '' ? '' : String(nextValue),
          type: 'number',
        },
      });
    }
  };

  const commitValue = (raw) => {
    const parsed = parseNumericValue(raw, { allowDecimal, allowEmpty, min });
    if (parsed === '') {
      setDisplayValue('');
      emitChange('');
      return;
    }
    const clamped = clampValue(parsed, min, max);
    setDisplayValue(String(clamped));
    emitChange(clamped);
  };

  const handleFocus = (event) => {
    setIsFocused(true);
    event.target.select();
    onFocusProp?.(event);
    inputPropsProp.onFocus?.(event);
  };

  const handleChange = (event) => {
    const raw = event.target.value;
    if (!pattern.test(raw)) return;
    setDisplayValue(raw);

    if (name) {
      emitChange(raw);
    }
  };

  const handleBlur = (event) => {
    setIsFocused(false);
    commitValue(displayValue);
    onBlurProp?.(event);
    inputPropsProp.onBlur?.(event);
  };

  return (
    <TextField
      {...textFieldProps}
      name={name}
      type="text"
      inputMode={allowDecimal ? 'decimal' : 'numeric'}
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      inputProps={{
        ...inputPropsProp,
        pattern: allowDecimal ? undefined : '[0-9]*',
        inputMode: allowDecimal ? 'decimal' : 'numeric',
      }}
      InputProps={InputPropsProp}
    />
  );
}

export default NumericTextField;
